/**
 * AgentListPage
 *
 * Displays a grid of the current user's AI agents with status indicators,
 * quick stats (knowledge sources, conversations), and actions (edit, delete,
 * toggle active/inactive).  Links to AgentBuilderPage for creating new agents.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  CContainer,
} from '@coreui/react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import config from '../../../utils/EnvironmentSwitcher';
import AgentMissionControlBoard from './components/AgentMissionControlBoard';

const AgentListPage = () => {
  const [agents, setAgents] = useState([]);
  const [activityItems, setActivityItems] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  /**
   * Build a small cross-agent activity feed from recent conversation logs.
   * We cap concurrent requests to keep this page responsive.
   */
  const fetchRecentActivity = useCallback(async (agentList, token) => {
    if (!Array.isArray(agentList) || agentList.length === 0) {
      setActivityItems([]);
      return;
    }

    setActivityLoading(true);
    try {
      const candidates = agentList.slice(0, 8);
      const perAgent = await Promise.all(
        candidates.map(async (agent) => {
          try {
            const res = await fetch(`${config.apiUrl}/api/agents/${agent.id}/conversations`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) return [];
            const data = await res.json();
            return (data.conversations || []).slice(0, 3).map((conversation) => ({
              id: `${agent.id}:${conversation.id || conversation.task_id || conversation.conversation_id || Date.now()}`,
              agentId: agent.id,
              agentName: agent.name || 'Agent',
              summary: conversation.summary || 'Conversation update',
              sentiment: conversation.sentiment || 'neutral',
              createdAt: conversation.createdAt || null,
            }));
          } catch (conversationErr) {
            console.warn('AgentListPage: conversation activity fetch warning', conversationErr);
            return [];
          }
        }),
      );

      const merged = perAgent
        .flat()
        .sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')))
        .slice(0, 8);

      setActivityItems(merged);
    } finally {
      setActivityLoading(false);
    }
  }, []);

  /**
   * Fetch agents from the backend API.
   */
  const fetchAgents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        setError('You must be logged in to view agents.');
        return;
      }
      const token = await user.getIdToken();
      const res = await fetch(`${config.apiUrl}/api/agents`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        throw new Error(`Failed to fetch agents: ${res.status}`);
      }
      const data = await res.json();
      const nextAgents = data.agents || [];
      setAgents(nextAgents);
      // Fetch activity after agents are loaded so the panel shows real recent conversation events.
      await fetchRecentActivity(nextAgents, token);
    } catch (err) {
      console.error('AgentListPage: fetchAgents error', err);
      setError(err.message);
      setActivityItems([]);
    } finally {
      setLoading(false);
    }
  }, [fetchRecentActivity]);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  /**
   * Delete an agent after confirmation.
   */
  const handleDelete = async (agentId, agentName) => {
    if (!window.confirm(`Delete agent "${agentName}"? This will remove all knowledge and conversation data.`)) {
      return;
    }
    try {
      const auth = getAuth();
      const token = await auth.currentUser.getIdToken();
      const res = await fetch(`${config.apiUrl}/api/agents/${agentId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Delete failed');
      // Remove from local state optimistically
      setAgents((prev) => prev.filter((a) => a.id !== agentId));
    } catch (err) {
      console.error('AgentListPage: delete error', err);
      alert('Failed to delete agent. Please try again.');
    }
  };

  /**
   * Toggle an agent between active and inactive status.
   */
  const handleToggleStatus = async (agent) => {
    try {
      const auth = getAuth();
      const token = await auth.currentUser.getIdToken();
      const newStatus = agent.status === 'active' ? 'inactive' : 'active';
      const res = await fetch(`${config.apiUrl}/api/agents/${agent.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error('Update failed');
      // Optimistic update
      setAgents((prev) =>
        prev.map((a) => (a.id === agent.id ? { ...a, status: newStatus } : a)),
      );
    } catch (err) {
      console.error('AgentListPage: toggle status error', err);
    }
  };

  /* ---- Render ----------------------------------------------------------- */

  if (loading) {
    return (
      <CContainer className="py-4">
        <AgentMissionControlBoard
          agents={[]}
          loading
          error={null}
          activityItems={[]}
          activityLoading
          officeName="Agent Mission Control"
        />
      </CContainer>
    );
  }

  return (
    <CContainer className="py-4">
      <AgentMissionControlBoard
        agents={agents}
        loading={false}
        error={error}
        activityItems={activityItems}
        activityLoading={activityLoading}
        officeName="Agent Mission Control"
        onCreateAgent={() => navigate('/dashboard/agents/new')}
        onOpenAgent={(agent) => navigate(`/dashboard/agents/${agent.id}`)}
        onToggleStatus={handleToggleStatus}
        onDeleteAgent={(agent) => handleDelete(agent.id, agent.name)}
        onRefresh={fetchAgents}
      />
    </CContainer>
  );
};

export default AgentListPage;


